import React, { useRef, useState, forwardRef, useImperativeHandle, useMemo } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import DateFnsUtils from '@date-io/date-fns';
import plLocale from 'date-fns/locale/pl';
import enLocale from 'date-fns/locale/en-US';
import format from 'date-fns/format';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { ReactComponent as CalendarIcon } from 'common-assets/assets/icons/CalendarBlank.svg';
import { FieldErrorMessage } from '../form/FieldErrorMessage/FieldErrorMessage';
import { FieldLabel } from '../form/FieldLabel/FieldLabel';
import { useFocusVisibleRef, useUniqueWcagValues } from 'common-react';
import { isDatePickerValidDate } from './tools/DatePicker.utils';
import { DatePickerPropsModel, DatePickerRefModel } from './tools/DatePicker.model';
import { DatePickerFormats, DatePickerPlaceholders, DatePickerSize } from './tools/DatePicker.const';
import { Icon } from '../Icon/Icon';
import { ReactComponent as RightArrow } from 'common-assets/assets/icons/arrow-right-filled.svg';
import { ReactComponent as LeftArrow } from 'common-assets/assets/icons/arrow-left-filled.svg';
import { useIsMobile } from '../utils/useIsMobile';
import { DatePickerToolbar } from './DatePickerToolbar';

import useStyles from './DatePicker.style';

const localeMap = {
  en: enLocale,
  pl: plLocale
};

interface DateFnsUtilsOptions {
  locale?: Locale;
  instance?: Date;
  formats?: Record<string, string>;
}

class LocalizedUtils extends DateFnsUtils {
  constructor(options?: DateFnsUtilsOptions) {
    super(options);
    this.format = (date: Date, formatKey: any) => {
      const formatString = this.formats[formatKey as keyof typeof this.formats] ?? formatKey;
      return this.formatByString(date, formatString);
    };

    // Override formatByString to use safe formats that don't contain unescaped 'n'
    this.formatByString = (date: Date, formatString: string) => {
      // Handle undefined or null date
      if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
        return '';
      }

      // If formatString is undefined, use the default format
      if (!formatString) {
        formatString = DatePickerFormats.DEFAULT;
      }

      try {
        // Replace long formatters (PP, PPPP, etc.) with explicit safe formats
        // Polish locale's long formatters contain "do" and other words with 'n'
        let safeFormatString = formatString
          .replace(/PPPP/g, 'EEEE, d MMMM yyyy')  // Full date with weekday
          .replace(/PPP/g, 'd MMMM yyyy')          // Long date
          .replace(/PP/g, 'd MMM yyyy')            // Medium date
          .replace(/P/g, 'dd.MM.yyyy')             // Short date
          .replace(/pppp/g, 'EEEE, d MMMM yyyy HH:mm:ss')
          .replace(/ppp/g, 'd MMMM yyyy HH:mm:ss')
          .replace(/pp/g, 'd MMM yyyy HH:mm')
          .replace(/p/g, 'HH:mm')
          // Replace narrow day format (EEEEE) with short format (EEE) to avoid 'n'
          .replace(/EEEEE(?!E)/g, 'EEE');

        return format(date, safeFormatString, { locale: this.locale });
      } catch (error) {
        console.error('DatePicker formatting error:', error);
        return '';
      }
    };
  }

  getCalendarHeaderText(date: Date) {
    return format(date, 'LLLL yyyy', { locale: this.locale });
  }

  getDatePickerHeaderText(date: Date) {
    return format(date, 'd MMM yyyy', { locale: this.locale });
  }

  // Override to prevent Polish locale from using formats with unescaped 'n'
  // The Polish locale has 'n' as narrow day name (niedziela)
  public getWeekdays = (): string[] =>
    this.locale === plLocale
      ? ['Pn', 'Wt', 'Śr', 'Cz', 'Pt', 'Sb', 'Nd']
      : ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

  getWeekdayArray(date: Date) {
    // Return full day names without narrow format to avoid 'n' character
    return [0, 1, 2, 3, 4, 5, 6].map((dayOfWeek) => {
      const newDate = new Date(date);
      newDate.setDate(date.getDate() - date.getDay() + dayOfWeek);
      return this.formatByString(newDate, 'EEEEEE'); // Use short format instead of narrow
    });
  }

  getDayText(date: Date) {
    return format(date, 'd', { locale: this.locale });
  }

  getYearText(date: Date) {
    return format(date, 'yyyy', { locale: this.locale });
  }
}

const localeUtilsMap = {
  en: LocalizedUtils,
  pl: LocalizedUtils,
};

export const DatePicker = forwardRef<DatePickerRefModel, DatePickerPropsModel>(
  (
    {
      onChange,
      value,
      name,
      className,
      errorMessage,
      classNameErrorMessage,
      validateOnChange = true,
      error = false,
      onInputKeyDown,
      onButtonKeyDown,
      autoInputTestId,
      label,
      sizes = DatePickerSize.DEFAULT,
      testId,
      format = DatePickerFormats.DEFAULT,
      placeholder = DatePickerPlaceholders.DEFAULT,
      ...props
    },
    ref
  ) => {
    const [, i18n] = useTranslation();
    const [tt] = useTranslation("components");
    const [isInvalidDate, setIsInvalidDate] = useState(false);
    const isMobile = useIsMobile();
    const hasError = useMemo(
      () => error || errorMessage || isInvalidDate,
      [error, errorMessage, isInvalidDate]
    );
    const { isFocusVisible, ref: focusVisibleRef } = useFocusVisibleRef();
    const c = useStyles({ sizes });
    const inputRef = useRef<HTMLInputElement>(null);
    const calendarButtonRef = useRef<HTMLButtonElement>(null);
    const { getUniqueValue } = useUniqueWcagValues();
    const uniqueId = getUniqueValue(name || "");
    const uniqueErrorId = getUniqueValue(name ? `${name}-error` : "error");

    useImperativeHandle(ref, () => ({
      inputRef,
      calendarButtonRef,
    }));

    const handleChangeWithValidate = (date: MaterialUiPickersDate) => {
      if (!isDatePickerValidDate(date) && !errorMessage) {
        setIsInvalidDate(true);
      } else {
        setIsInvalidDate(false);
        onChange?.(date);
      }
    };

    const handleChange = (date: MaterialUiPickersDate) => {
      onChange?.(date);
    };

    const InputPropsConfiguration = {
      disableUnderline: true,
      className: clsx(c.inputDatePicker, className, hasError && "Mui-error", isFocusVisible && 'Mui-focusVisible'),
      id: uniqueId,
      ref: inputRef,
      onKeyDown: onInputKeyDown,
    } as const;

    const inputPropsConfiguration = {
      "data-autotestid": autoInputTestId,
      "aria-labelledby": error ? uniqueErrorId : undefined,
      ref: focusVisibleRef
    } as const;

    const KeyboardButtonPropsConfiguration = {
      className: c.calendarButton,
      "aria-label": tt("datepicker.choose-date"),
      ref: calendarButtonRef,
      onKeyDown: onButtonKeyDown,
    } as const;

    const InputAdornmentPropsConfiguration = {
      position: "end",
    } as const;

    const onlyYearOrMonth = props?.views?.includes('year') || props?.views?.includes('month');

    return (
      <div className={c.root}>
        <MuiPickersUtilsProvider
          utils={
            localeUtilsMap[i18n.language as keyof typeof localeUtilsMap] ||
            localeUtilsMap["pl"]
          }
          locale={
            localeMap[i18n.language as keyof typeof localeMap] ||
            localeMap["pl"]
          }
        >
          {label && <FieldLabel htmlFor={uniqueId}>{label}</FieldLabel>}
          <KeyboardDatePicker
            InputProps={InputPropsConfiguration}
            inputProps={inputPropsConfiguration}
            KeyboardButtonProps={KeyboardButtonPropsConfiguration}
            leftArrowIcon={<Icon><LeftArrow /></Icon>}
            rightArrowIcon={<Icon><RightArrow /></Icon>}
            keyboardIcon={
              <Icon>
                <CalendarIcon width={24} height={24} />
              </Icon>
            }
            PopoverProps={{ className: clsx({
              [c.datePickerPopover]: true,
              [c.datePickerPopoverWithToolbar]: onlyYearOrMonth,
            })}}
            disableToolbar={!onlyYearOrMonth}
            variant={isMobile ? 'dialog' : "inline"}
            format={format}
            placeholder={placeholder}
            ToolbarComponent={onlyYearOrMonth ? DatePickerToolbar : undefined}
            value={value}
            onChange={(date) =>
              validateOnChange
                ? handleChangeWithValidate(date)
                : handleChange(date)
            }
            name={name}
            autoOk={!isMobile}
            cancelLabel={isMobile ? tt("datepicker.cancel-mobile-button-label") : undefined}
            okLabel={isMobile ? tt("datepicker.confirm-mobile-button-label") : undefined}
            invalidDateMessage=""
            minDateMessage=""
            maxDateMessage=""
            InputAdornmentProps={InputAdornmentPropsConfiguration}
            DialogProps={{
              className: clsx({
                [c.datePickerPopover]: true,
                [c.datePickerPopoverWithToolbar]: onlyYearOrMonth,
              }),
            }}
            // Label extracted to FieldLabel component
            label=""
            data-testid={testId}
            {...props}
          />
          {(!!errorMessage || isInvalidDate) ? (
            <FieldErrorMessage className={classNameErrorMessage} id={uniqueErrorId}>
              {errorMessage || tt("datepicker.invalid-date")}
            </FieldErrorMessage>
          ) : null}
        </MuiPickersUtilsProvider>
      </div>
    );
  }
);
